import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { Popover } from 'primeng/popover';
import { NavigationEnd, Router, ActivatedRoute } from '@angular/router';
import { SidebarService, SessionService, SpeechRecognitionService, OAuthService, AppService, PreferencesApplierService, LoginReturnService } from './services';
import { AmSuiteTelemetryService, AmSuiteTelemetryProfile } from '@neworg-net/amsuite-core/telemetry';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { AuthSessionService, MsalCookieSyncService } from '@neworg-net/amsuite-core/auth';
import { LegacySessionService } from './services/legacy-session.service';
import { NotificationNavigationService } from './services/notification-navigation.service';
import { normalizePrimeIconClass } from './shared/prime-icon.util';

import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { DatePickerLocale } from './models';
import { AppStateHolder, TimeLine } from './shared';
import { UserProperty, CompanyProperty, AppProperty } from './properties';
import { apps } from './constants';
import { interval, Subscription, lastValueFrom, filter, map, mergeMap, Subject, takeUntil, take, fromEvent, combineLatest, merge, of, EMPTY, switchMap, startWith, distinctUntilChanged } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SwUpdateService } from './services/pwa/sw-update.service';
import { formatDateInTimeZone, resolveTimeZone, toValidDate } from './shared/date-time.util';
import { bindRouteNavigationProgress } from './shared/chunk-load-recovery';

/** Key del p-toast dell'avvio non riuscito in app.component.html, l'unico col pulsante «Riprova». */
const STARTUP_ERROR_TOAST = 'startupError';

// NO ChangeDetectionStrategy.OnPush: la shell e il suo sottoalbero ospitano le librerie
// @neworg (compilate default-CD) e leggono getter mutabili di AppProperty/UserProperty nei
// template; una radice OnPush non-dirty le esclude dal tick e congela dashboard e widget.
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class AppComponent implements OnInit, OnDestroy {
  /**
   * Chiude il toast su cui si clicca, inoltrando il click al suo pulsante di chiusura.
   *
   * Non si usa `(click)="MessageService.clear()"` sul `p-toast`: `clear()` nasconde il messaggio
   * senza impostare `isClosing` dentro `ToastItem`, al primo `mouseleave` il timer riparte e il
   * toast riappare da solo (visto in dialog, commit aa919cb83). `onCloseIconClick` e' l'unico
   * percorso che alza quel flag, quindi la chiusura passa di li'. In piu' `clear()` senza argomenti
   * svuota OGNI toast montato, anche quelli con key dedicata: qui si chiude solo il messaggio
   * cliccato, e un messaggio `closable: false` (senza pulsante) resta dov'e'.
   *
   * Vive nel root e non nelle singole pagine perche' il difetto e' del componente PrimeNG, non
   * della pagina: vale per tutti i toast dell'app, presenti e futuri, compresi quelli montati
   * dalle librerie @neworg-net.
   */
  @HostListener('document:click', ['$event'])
  protected dismissClickedToast(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const message = target?.closest?.('.p-toast-message');

    // Sul pulsante di chiusura ci pensa gia' PrimeNG: reinoltrare sarebbe un giro a vuoto.
    if (!message || target?.closest?.('.p-toast-close-button')) {
      return;
    }

    message.querySelector<HTMLElement>('.p-toast-close-button')?.click();
  }

  private static readonly OAUTH_POPUP_POLL_MS = 500;
  private static readonly OAUTH_POPUP_TIMEOUT_MS = 600_000;

  sidebarSrv = inject(SidebarService);
  sessionService = inject(SessionService);
  private _translator = inject(TranslateService);
  private _messageService = inject(MessageService);
  private _primeConfig = inject(PrimeNG);
  appStateHolder = inject(AppStateHolder);
  private _appService = inject(AppService);
  userProperty = inject(UserProperty);
  private companyProperties = inject(CompanyProperty);
  private _router = inject(Router);
  private _timelime = inject(TimeLine);
  private activatedRoute = inject(ActivatedRoute);
  appProperty = inject(AppProperty);
  private swUpdate = inject(SwUpdateService);
  private _appInsightsService = inject(AmSuiteTelemetryService);
  speechRecognitionService = inject(SpeechRecognitionService);
  private _oauthService = inject(OAuthService);
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private msalCookieSync = inject(MsalCookieSyncService);
  private authSessionService = inject(AuthSessionService);
  private _preferencesApplier = inject(PreferencesApplierService);
  private legacySessionService = inject(LegacySessionService);
  private readonly notificationNavigation = inject(NotificationNavigationService);
  private readonly loginReturn = inject(LoginReturnService);

  appIconClass(app: any): string {
    return normalizePrimeIconClass(app?.cssParams?.menuIconClass);
  }

  @HostListener('window: resize', ['$event'])
  onresize(event) {
    this.innerWidth = window.innerWidth;
  }

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {

    this._translator.setDefaultLang('it');
    this._primeConfig.setTranslation(DatePickerLocale.it);

    // Oltre al check, il servizio gestisce VERSION_READY (prompt aggiornamento) e
    // UNRECOVERABLE_STATE (reload con anti-loop). amsuite e' l'host degli iframe: una
    // shell PWA stale trascina tutte le app satellite, qui il recovery e' critico.
    this.swUpdate.checkForUpdate();
    bindRouteNavigationProgress(this._router, this.isNavigating);
  }

  readonly isNavigating = signal(true);
  impersonateLoader: boolean = false;
  innerWidth: any;

  subscription: Subscription;
  subscriptionImpersonate: Subscription;

  visibleSidebar: boolean = false;
  visibleAlertSidebar: boolean = false;
  visibleCopilotSidebar: boolean = false;
  visibleSupportSidebar: boolean = false;

  activity: any;

  alertsCount: any = 0;

  showAIIcon: boolean = false;

  userAuthProfile: AmSuiteTelemetryProfile = { userName: '', userFullName: '' };
  authProfileMapped: boolean = false;
  private routerEventsSubscribed = false;

  private readonly destroy$ = new Subject<void>();

  idChat: string = '';
  amAssistantBotAppId: string = "23A331CD-85FD-4F72-A764-AAA3099CDF7A";
  bearerToken: string | null = null;

  @ViewChild('oauthPanel') oauthPanel: Popover;
  selectedOAuthProvider: string = null;
  oauthAuthorizingProvider: string = null;
  oauthAutoRefreshSub: Subscription | null = null;
  private oauthPopupPollTimer: ReturnType<typeof setInterval> | null = null;
  private oauthPopupTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  private oauthPopupWindow: Window | null = null;
  private oauthPopupFinalized = true;
  impersonateAutoRefreshSub: Subscription | null = null;
  private lastImpersonateState: boolean | null = null;

  ngOnInit() {
    try {
      this.appProperty.loadingData.set(true);
      this.appProperty.setAppClass('app-orange');
      this.appProperty.setAppName('AmSuite');
      this.innerWidth = window.innerWidth;

      this.sidebarSrv.showSidebar = false;
      this.sidebarSrv.subscribeEventsNavigation();
      this._timelime.init();
    } catch (error: any) {
      if (this.legacySessionService.isModernSessionValid() && this.legacySessionService.isAuthErrorForLegacy(error)) {
        void this.legacySessionService.probeAndEnsureValid();
      } else {
        this.authSessionService.reauthenticate('ngOnInit initialization error');
      }
    }

    this.bearerToken = this.getBearerToken();

    // Start MSAL cookie sync for cross-app backward compat
    this.msalCookieSync.startSync();

    // Consume del query param di ritorno da auth/login del backend.
    // Quando il backend completa l'OIDC flow, ridirige al frontend con ?cookieBootstrapped=true:
    // marchiamo la sessione e ripuliamo l'URL.
    this.consumeCookieBootstrapMarker();

    // MSAL è già inizializzato in main.ts (handleRedirectPromise completato).
    // getAllAccounts() è affidabile — non serve aspettare inProgress$.
    const accounts = this.msalService.instance.getAllAccounts();
    const hasAccount = accounts.length > 0;
    const onLoginRoute =
      this._router.url === '/login' || this._router.url.startsWith('/login/');

    // Se MSAL ha autenticato ma il backend legacy non ha ancora emesso il cookie .AspNet.Cookies,
    // facciamo un giro server-side via auth/login per generarlo (B2C in SSO trasparente).
    // Le app satellite (CRM, Calendar, ecc.) leggono questo cookie per autenticarsi.
    if (hasAccount && !this.isCookieBootstrapped() && !onLoginRoute && !this.authSessionService.isReauthInProgress()) {
      this.bootstrapBackendCookie();
      return;
    }

    if (hasAccount) {
      // Dopo auth moderno, verifica subito lo stato della sessione legacy ASP.
      // Se scaduta mentre Bearer è valido → mostriamo il banner "Sessione scaduta"
      // esattamente come per il caso moderno (solo show, niente redirect strano).
      // L'utente andrà al login nuovo dal banner; il bootstrap legacy iniziale
      // verrà eseguito di nuovo normalmente dopo il login.
      void this.legacySessionService.probeAndEnsureValid();

      this.loadUserData();
    }
  }

  private consumeCookieBootstrapMarker(): void {
    const params = new URLSearchParams(window.location.search);
    if (params.get('cookieBootstrapped') !== 'true') return;

    sessionStorage.setItem('cookieBootstrapped', 'true');

    params.delete('cookieBootstrapped');
    const cleanSearch = params.toString();
    const cleanUrl = window.location.pathname
      + (cleanSearch ? '?' + cleanSearch : '')
      + window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
  }

  private isCookieBootstrapped(): boolean {
    return sessionStorage.getItem('cookieBootstrapped') === 'true';
  }

  private bootstrapBackendCookie(): void {
    // Delega al servizio centralizzato (supporta anche il caso "legacy scaduta mentre moderno OK").
    // Preserva deep-link e flag cookieBootstrapped.
    this._appService.forceLegacyCookieBootstrap();
  }

  private loadUserData(): void {
    // Ogni chiamata di avvio ha un esito anche per l'errore, o «Caricamento...» resterebbe a
    // schermo. Le scadenze di sessione (401, 504, legacy) riautenticano o mostrano il banner;
    // ogni altro errore passa da failStartup.
    const failStartup = this.startupFailureHandler();
    lastValueFrom(this._appService.getRealUser()).then(
      async realUser => {
        this.userProperty.setRealUser(realUser);
        this.getAssistantPermissions();

        const defaultLanguage = realUser.defaultLanguage === 'en' ? 'en-US' : 'it-IT';
        this.speechRecognitionService.updateLanguage(defaultLanguage);

        this._appService.impersonateActive().subscribe({
          next: s => {
            this.appStateHolder.removeCookie('ImpersonateState');

            this.appProperty.setIsImpersonateActive(s);
            this.appStateHolder.setCookie('ImpersonateState', s.toString());

            if (s) {
              this._appService.getUser().subscribe({
                next: currentUser => {
                  this.setUserParams(currentUser);
                },
                error: failStartup,
              })
            } else {
              this.setUserParams(realUser);
            }

            // Set authType cookie for backward compat (always b2c now)
            this.appStateHolder.setCookie('authType', 'b2c');
            // Update bearer token from cookie (synced by MsalCookieSyncService)
            this.bearerToken = this.getBearerToken();

            this._appService.getDataOwner().then(async dataOwner => {
              this.userProperty.dataOwnerId = dataOwner;
              this.mapAppInsightModel();
              // Preferenze Workspace (cascata): applica lingua/menu effettivi sopra al legacy.
              // L'applier non rilancia: se KO restano i valori legacy già applicati da setUserParams.
              this._preferencesApplier.applyEffective().subscribe();
              this.getOAuthConfiguration();
              // Start auto-refresh of OAuth providers after initial load
              this.initOAuthProvidersAutoRefresh();
              // Start auto-refresh of impersonation state after initial load
              this.lastImpersonateState = s;
              this.initImpersonateAutoRefresh();
              await lastValueFrom(this._appService.getUserCompanies()).then(result => {
                this.appStateHolder.mapDevURL(result)
                this.userProperty.setUserCompanies(result);
                let company: any;

                if (dataOwner)
                  company = this.userProperty.userCompaniesList.find(c => { return c.id.toLowerCase() === dataOwner.toLowerCase() });

                if (!company || !dataOwner)
                  company = this.userProperty.userCompaniesList[0];

                this.appProperty.loadingData.set(false);
                this.onSelectCompany(company);

                // Probe post-load: rileva se la sessione ASP legacy è già scaduta
                // (mentre il moderno è ancora valido). Mostra banner identico al Bearer.
                void this.legacySessionService.probeAndEnsureValid();
              });
            }).catch((err: any) => {
              // Se getDataOwner fallisce per sessione legacy scaduta (moderno OK), mostra il banner "Sessione scaduta" (stesso del caso moderno).
              if (this.legacySessionService.isModernSessionValid() && this.legacySessionService.isAuthErrorForLegacy(err)) {
                void this.legacySessionService.probeAndEnsureValid();
              } else {
                // Owner o aziende non letti per un altro motivo (500, rete): si puo' riprovare.
                failStartup();
              }
            });
          },
          error: failStartup,
        })

        this._appService.getUserParty().subscribe({
          next: party => {
            this.userProperty.setUserParty(party);
          },
          error: failStartup,
        });

      }).catch(error => {
        if (error && (error.status === 401 || error.status === 504)) {
          // Distingui sessione legacy ASP scaduta (mentre moderno/Bearer ancora valido)
          // dal caso di scadenza moderna. Mostriamo lo stesso banner "Sessione scaduta".
          if (this.legacySessionService.isModernSessionValid() &&
              this.legacySessionService.isAuthErrorForLegacy(error)) {
            void this.legacySessionService.probeAndEnsureValid();
            return;
          }
          this.authSessionService.reauthenticate(
            `loadUserData failed: ${error.status}`
          );
        } else {
          // Non e' una scadenza (500, rete assente, risposta illeggibile): si puo' riprovare.
          failStartup();
        }
      });
  }

  /**
   * Esito d'errore di un tentativo di avvio: il caricamento si spegne e compare l'avviso con
   * «Riprova». Piu' chiamate dello stesso tentativo possono fallire insieme (per esempio
   * l'impersonificazione e l'anagrafica, che partono in parallelo): l'avviso parte una volta.
   */
  private startupFailureHandler(): () => void {
    let notified = false;
    return () => {
      this.appProperty.loadingData.set(false);
      if (notified) {
        return;
      }
      notified = true;
      // Testi e azione li scrive il template del toast con la pipe translate: l'avvio puo'
      // fallire prima che arrivino le traduzioni, e instant() darebbe le chiavi.
      this._messageService.add({ key: STARTUP_ERROR_TOAST, severity: 'warn', sticky: true });
    };
  }

  /**
   * «Riprova» dell'avviso di avvio: rilegge i dati della sessione col caricamento a schermo.
   * Un secondo clic mentre il nuovo tentativo e' in corso non ne avvia un altro.
   */
  retryLoadUserData(): void {
    if (this.appProperty.loadingData()) {
      return;
    }
    this.appProperty.loadingData.set(true);
    this.loadUserData();
  }

  getAssistantPermissions(): void {
    this.showAIIcon = false;
    this._appService.getAppUserPermissions(this.amAssistantBotAppId).subscribe({
      next: (response) => {
        if (response && response.length > 0)
          this.showAIIcon = true;
      },
      error: () => {
      }
    });
  }

  mapAppInsightModel() {
    if (!this.userProperty.realUserModel
      || !this.userProperty.realUserModel.userName
      || !this.userProperty.realUserModel.fullName
      || !this.userProperty.dataOwnerId
    )
      return;

    this.authProfileMapped = true;
    this.userAuthProfile = {
      userName: this.userProperty.realUserModel.userName,
      userFullName: this.userProperty.realUserModel.fullName,
      authType: 'b2c',
      authDomain: this.appStateHolder.getCookie('authTypeDomain'),
      dataOwnerId: this.userProperty.dataOwnerId,
      impersonateState: this.appStateHolder.getCookie('ImpersonateState'),
    };

    this._appInsightsService.setUserAuthProfile(this.userAuthProfile)

    this.subscribeRouterEvents()

  }

  setUserParams(user: any) {
    const defaultLanguage = this.normalizeUserLanguage(user?.defaultLanguage);
    const normalizedUser = { ...user, defaultLanguage };

    this._translator.setDefaultLang(defaultLanguage);
    this.setPrimeConfigSettings(defaultLanguage);
    this.sidebarSrv.setExpand(user?.menuState);
    this.sessionService.setSession(normalizedUser);
  }

  private normalizeUserLanguage(language: string | null | undefined): 'en' | 'it' {
    const normalizedLanguage = language?.toLowerCase();
    return normalizedLanguage === 'en' || normalizedLanguage === 'en-us' ? 'en' : 'it';
  }

  subscribeRouterEvents() {
    // «Riprova» dell'avvio ripassa da mapAppInsightModel: le sottoscrizioni al router restano
    // una sola, altrimenti ogni pagina andrebbe in telemetria una volta per tentativo.
    if (this.routerEventsSubscribed) {
      return;
    }
    this.routerEventsSubscribed = true;

    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter(route => route.outlet === 'primary'),
        mergeMap(route => route.data)
      )
      .subscribe(event => {
        const title = event['title'];
        if (title) {
          const pageName = 'AmSuite - ' + title;
          this._appInsightsService.trackPageView(pageName);
        }
      });

    // L'identita' cromatica dell'header viene impostata entrando in un'app
    // (setAppNavbarClass / onSelectApp) ma non veniva mai ripristinata uscendone: il
    // back del browser non passa da onSelectApp, quindi tornando in dashboard dal
    // dettaglio aperto da un widget l'header restava del colore dell'app lasciata.
    // Derivarla dalla rotta corrente copre ogni via di uscita, back incluso.
    this._router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => {
        if (!this.appStateHolder.isHostedAppRoute(event.urlAfterRedirects)) {
          this.appStateHolder.resetAppNavbarClass();
        }
      });
  }


  //#region App


  /**
   * Sets the selected company and performs necessary actions.
   * @param c The company object to be selected.
   */
  async onSelectCompany(c): Promise<void> {
    if (!c?.id) return;
    try {
      await this._appService.setDataOwner(c.id);
      this.userProperty.dataOwnerId = c.id;
      this.companyProperties.setCompanyApps(c.apps);
      this.userProperty.setSelectedCompany(c);
      this.appProperty.dataLoaded.set(true);

      // Ritorno all'app da cui la guard ha mandato alla login della Suite (#2161): a
      // questo punto la sessione e' completa e il cookie che l'app legge e' scritto.
      if (await this.loginReturn.resumePending()) {
        return;
      }

      // Riprende il click della campanella soltanto dopo il catalogo del nuovo contesto.
      // Il servizio verifica di nuovo utente, scadenza, azienda e disponibilità dell'app.
      if (await this.notificationNavigation.resumePending()) {
        this.appProperty.loginPageNotVisible.set(true);
        return;
      }

      // Deep-link preservation per le rotte /comunica (es. link aperto dalla mail).
      // Va preservato su OGNI arrivo, non solo al primo: l'utente puo` aprire il
      // link con AmSuite gia` attivo nello stesso tab (la vecchia guardia su
      // "primo arrivo" qui cadeva e onSelectApp('/') portava in dashboard,
      // perdendo la news) oppure fare F5 sulla pagina della news.
      const currentUrl = this._router.url;

      if (currentUrl.startsWith('/comunica')) {
        this.appProperty.loginPageNotVisible.set(true);
        return;
      }

      this.onSelectApp({ url: "/" });
    } catch {
      this._messageService.add({
        severity: 'warn',
        summary: this._translator.instant('notifications.navigation.title'),
        detail: this._translator.instant('notifications.navigation.company-unavailable'),
      });
    }
  }


  /**
   * Handles the selection of an app.
   * @param app The selected app object.
   * @returns void
   */
  onSelectApp(app): void {
    const appClass = app.cssParams ? app.cssParams.appClass : 'app-orange';
    const appName = app.functionName && app.functionName != 'AmSuite' ? 'AmSuite ' + app.functionName : 'AmSuite';

    this.appProperty.setAppClass(appClass);
    this.appProperty.setAppName(appName);

    if (app.url === "/") {
      this._router.navigate(['/']);
      return;
    }

    this._router.navigate(['app', app.id]);
  }


  /**
   * Changes the selected company and updates the data owner.
   * @param {any} company - The company object to be selected.
   * @returns {Promise<void>} - A promise that resolves when the data owner is updated.
   */
  changeCompany(company) {
    this._appService.setDataOwner(company.id).then(() => {
      this.companyProperties.setCompanyApps(company.apps);

      this.userProperty.setSelectedCompany(company);
      window.location.href = '/dashboard';
    });
  }

  //#endregion

  //#region Alert

  showNotify() {
  }


  /**
   * Updates the alerts count and performs additional actions.
   * @param event - The event object containing the updated alerts count.
   */
  updateAlertsCount(event: any) {
    this.alertsCount = event;
    const element = <HTMLInputElement>document.getElementById("myButton");

    if (element) {
      element.disabled = false;
      element.click();
      element.disabled = true;
    }
  }


  //#endregion

  //#region Activity


  /**
   * Determines whether the create activity feature is enabled.
   *
   * @returns {boolean} True if the create activity feature is enabled, false otherwise.
   */
  enableCreateActivity(): boolean {
    if (this.companyProperties.userCompanyApps) {
      const workflow = this.companyProperties.userCompanyApps.find(app => { return app.id.toLowerCase() == apps.calendar.toLowerCase() })
      if (workflow != undefined)
        return true;
      else
        return false;
    }
    return false;
  }


  /**
   * Creates an activity.
   */
  createActivity() {
    this._appService.createActivity().subscribe({
      next: act => {
        this.activity = act;
        this.activity.isNew = true;
        const d = this._timelime.day.date;

        d.setHours(d.getHours() - d.getTimezoneOffset() / 60);
        this.activity.date = this.activity.expiryDate = d.toISOString();
      }
    });
  }


  /**
   * Handles the saving of an activity based on the specified option.
   *
   * @param option - The option indicating the action to be taken.
   *                1 - Clear the activity.
   *                2 - Create a new activity.
   *                3 - Navigate to the activity page.
   */
  onActivitySaved(option: number) {
    if (option == 1) {
      this.activity = null;
      return
    }
    if (option == 2) {
      this.createActivity();
      return;
    }
    if (option == 3) {
      this.activity = null;

      const url = this.appStateHolder.getAppUrl(apps.calendar);
      this.appStateHolder.setAppNavbarClass(apps.calendar)

      if (url.length === 0) {
        const link = ['/activity', this.appStateHolder.activityId];
        this._router.navigate(link)
        return;
      }

      const link = ['/app', `${url}/activity/${this.appStateHolder.activityId}`];
      this._router.navigate(link);
    }
  }

  onEditorClosed(event: any) {
    if (event)
      this.activity = null;
  }

  //#endregion

  //#region Impersonate
  // Impersonation state is seeded on init and kept in sync via initImpersonateAutoRefresh()
  //#endregion

  /**
   * Sets the PrimeNG configuration settings based on the specified language.
   * @param language The language code ('en' for English, 'it' for Italian).
   */
  setPrimeConfigSettings(language: any) {
    this._primeConfig.ripple.set(true);

    switch (language) {
      case 'en':
        this._primeConfig.setTranslation(DatePickerLocale.en);
        break;
      default:
        this._primeConfig.setTranslation(DatePickerLocale.it);
        break;
    }
  }

  /**
   * Removes the impersonation and reloads the page.
   */
  removeImpersonate() {
    this.impersonateLoader = true;

    const sessionId = this.appStateHolder.getCookie('ImpersonationSessionId')?.trim();

    this._appService.removeImpersonate().pipe(
      switchMap(() => {
        if (!sessionId) {
          return of(null);
        }

        return this._appService.revokeImpersonationSession(sessionId).pipe(
          catchError(() => of(null))
        );
      })
    ).subscribe({
      next: () => this.finishRemoveImpersonate(),
      error: () => {
        this.impersonateLoader = false;
      }
    });
  }

  private finishRemoveImpersonate(): void {
    this.appStateHolder.removeCookie('ImpersonationSessionId');
    this.appStateHolder.removeCookie('ImpersonateState');
    this.appProperty.setIsImpersonateActive(false);
    this.lastImpersonateState = false;
    this.impersonateLoader = false;
    this.userProperty.dataOwnerId = undefined;
    this.reloadWindow();
  }

  private reloadWindow(): void {
    window.location.reload();
  }

  getSidebarClass() {
    if (this.innerWidth <= 700)
      return 'w-12'
    else if (this.innerWidth <= 1000)
      return 'w-10'
    else if (this.innerWidth <= 1200)
      return 'w-9'
    else if (this.innerWidth <= 1400)
      return 'w-8'
    else if (this.innerWidth <= 1500)
      return 'w-7'
    else
      return 'w-5'
  }

  /** Su mobile i drawer entrano dal basso (bottom sheet a tutto schermo),
      su desktop restano laterali a destra. */
  get drawerPosition(): 'right' | 'bottom' {
    return this.innerWidth <= 640 ? 'bottom' : 'right';
  }

  /**
   * Load OAuth configuration and status for all providers
   */
  private getOAuthConfiguration(): void {
    this._oauthService.getProviders().subscribe({
      next: (providers) => {
        // Filter only active providers
        const activeProviders = providers.filter(provider => provider.isActive);
        this.appStateHolder.oauthState.providers = activeProviders;
      },
      error: () => {
      }
    });
  }

  /**
   * Show OAuth status panel
   */
  showOAuthPanel(event: any, provider: string): void {
    this.selectedOAuthProvider = provider;
    this.oauthPanel.toggle(event);
  }

  /**
   * Get OAuth provider status (active, expiring, expired)
   */
  getOAuthProviderStatus(providerName: string): 'active' | 'expiring' | 'expired' {
    const provider = this.appStateHolder.oauthState.providers.find(p => p.provider === providerName);

    if (!provider || !provider.hasValidToken) {
      return 'expired';
    }

    if (provider.refreshTokenExpiresAt) {
      const expiresAt = toValidDate(provider.refreshTokenExpiresAt);
      if (!expiresAt) {
        return 'expired';
      }
      const hoursUntilExpiry = Math.trunc((expiresAt.getTime() - Date.now()) / 3_600_000);

      if (hoursUntilExpiry <= 0) {
        return 'expired';
      } else if (hoursUntilExpiry <= 24) {
        return 'expiring';
      }
    }

    return 'active';
  }

  /**
   * Format OAuth expiry date
   */
  formatOAuthExpiryDate(providerName: string): string {
    const provider = this.appStateHolder.oauthState.providers.find(p => p.provider === providerName);
    if (provider?.refreshTokenExpiresAt) {
      const userTimezone = resolveTimeZone(this.sessionService.getTimezone);
      return formatDateInTimeZone(provider.refreshTokenExpiresAt, 'DD/MM/YYYY', userTimezone, 'it');
    }
    return '';
  }

  /**
   * Format OAuth expiry time
   */
  formatOAuthExpiryTime(providerName: string): string {
    const provider = this.appStateHolder.oauthState.providers.find(p => p.provider === providerName);
    if (provider?.refreshTokenExpiresAt) {
      const userTimezone = resolveTimeZone(this.sessionService.getTimezone);
      return formatDateInTimeZone(provider.refreshTokenExpiresAt, 'HH:mm', userTimezone, 'it');
    }
    return '';
  }

  /**
   * Authorize OAuth provider
   */
  authorizeOAuthProvider(provider: string): void {
    this.finalizeOAuthPopup(false, true);
    this.oauthAuthorizingProvider = provider;

    this._oauthService.authorize(provider).subscribe({
      next: (response) => {
        if (response.authorizationUrl) {
          // Open authorization URL in popup window
          const authWindow = window.open(
            response.authorizationUrl,
            '_blank',
            'location=yes,height=650,width=650,scrollbars=yes,status=yes'
          );

          if (authWindow) {
            this.oauthPopupWindow = authWindow;
            this.oauthPopupFinalized = false;
            // Monitor the popup window
            this.oauthPopupPollTimer = setInterval(() => {
              try {
                // Check if window is closed
                if (authWindow.closed) {
                  this.finalizeOAuthPopup(true, false);
                  return;
                }

                // Try to access the URL (will fail for cross-origin)
                const currentUrl = authWindow.location.href;

                // Check if we're back on our domain (successful callback)
                if (currentUrl.includes(window.location.hostname)) {
                  this.finalizeOAuthPopup(true, true);
                }
              } catch (e) {
                // Cross-origin error is expected, continue monitoring
              }
            }, AppComponent.OAUTH_POPUP_POLL_MS);

            // Cleanup interval after 10 minutes (timeout)
            this.oauthPopupTimeoutTimer = setTimeout(
              () => this.finalizeOAuthPopup(false, true),
              AppComponent.OAUTH_POPUP_TIMEOUT_MS,
            );
          } else {
            this.oauthAuthorizingProvider = null;
          }
        } else {
          this.oauthAuthorizingProvider = null;
        }
      },
      error: () => {
        this.oauthAuthorizingProvider = null;
      }
    });
  }

  private finalizeOAuthPopup(refreshProviders: boolean, closePopup: boolean): void {
    if (this.oauthPopupFinalized) {
      return;
    }

    this.oauthPopupFinalized = true;

    if (this.oauthPopupPollTimer !== null) {
      clearInterval(this.oauthPopupPollTimer);
      this.oauthPopupPollTimer = null;
    }
    if (this.oauthPopupTimeoutTimer !== null) {
      clearTimeout(this.oauthPopupTimeoutTimer);
      this.oauthPopupTimeoutTimer = null;
    }

    const popup = this.oauthPopupWindow;
    this.oauthPopupWindow = null;
    if (closePopup && popup && !popup.closed) {
      try {
        popup.close();
      } catch {
        // The popup can remain cross-origin until the browser closes it.
      }
    }

    this.oauthAuthorizingProvider = null;
    if (refreshProviders) {
      this.refreshOAuthProviders();
    }
  }

  /**
   * Refresh OAuth providers list
   */
  private refreshOAuthProviders(): void {
    this._oauthService.getProviders().subscribe({
      next: (providers) => {
        const activeProviders = providers.filter(provider => provider.isActive);
        this.appStateHolder.oauthState.providers = activeProviders;
      },
      error: () => {
      }
    });
  }

  /**
   * Initialize RxJS-based auto refresh for OAuth providers.
   * - Foreground: refresh every 60s
   * - Background: refresh every 5m (subject to browser throttling)
   * - Immediate refresh on focus/visible/online
   */
  private initOAuthProvidersAutoRefresh(): void {
    if (this.oauthAutoRefreshSub) return; // already running

    const visible$ = fromEvent(document, 'visibilitychange').pipe(
      map(() => !document.hidden),
      startWith(!document.hidden),
      distinctUntilChanged()
    );

    const focus$ = fromEvent(window, 'focus');

    const online$ = merge(
      of(navigator.onLine),
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false))
    ).pipe(distinctUntilChanged());

    const periodic$ = combineLatest([visible$, online$]).pipe(
      switchMap(([visible, online]) => online
        ? interval(visible ? 120000 : 300000)
        : EMPTY
      )
    );

    const tick$ = merge(
      of(0), // initial immediate refresh
      focus$,
      visible$.pipe(filter(v => v)), // when becomes visible
      online$.pipe(filter(o => o)), // when back online
      periodic$
    );

    // L'errore resta sulla singola rilettura: quel giro si salta e il successivo rilegge. Dopo lo
    // switchMap, il primo errore chiuderebbe per sempre focus, rete e timer.
    this.oauthAutoRefreshSub = tick$.pipe(
      switchMap(() => this._oauthService.getProviders().pipe(
        map(providers => providers.filter(p => p.isActive)),
        catchError(() => EMPTY),
      )),
    ).subscribe(activeProviders => {
      this.appStateHolder.oauthState.providers = activeProviders;
    });
  }

  /**
   * Initialize RxJS-based auto refresh for Impersonation status.
   * - Foreground: refresh every 60s
   * - Background: refresh every 5m (subject to browser throttling)
   * - Immediate refresh on focus/visible/online
   */
  private initImpersonateAutoRefresh(): void {
    if (this.impersonateAutoRefreshSub) return; // already running

    const visible$ = fromEvent(document, 'visibilitychange').pipe(
      map(() => !document.hidden),
      startWith(!document.hidden),
      distinctUntilChanged()
    );

    const focus$ = fromEvent(window, 'focus');

    const online$ = merge(
      of(navigator.onLine),
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false))
    ).pipe(distinctUntilChanged());

    const periodic$ = combineLatest([visible$, online$]).pipe(
      switchMap(([visible, online]) => online
        ? interval(visible ? 120000 : 300000)
        : EMPTY
      )
    );

    const tick$ = merge(
      focus$,
      visible$.pipe(filter(v => v)),
      online$.pipe(filter(o => o)),
      periodic$
    );

    // Come per i provider: un errore salta solo questa riverifica.
    this.impersonateAutoRefreshSub = tick$.pipe(
      switchMap(() => this._appService.impersonateActive().pipe(catchError(() => EMPTY))),
    ).subscribe((s: boolean) => {
      // Update holder + cookie
      this.appProperty.setIsImpersonateActive(s);
      this.appStateHolder.removeCookie('ImpersonateState');
      this.appStateHolder.setCookie('ImpersonateState', s?.toString?.() ?? 'false');

      // Update user session only when state actually changes
      if (this.lastImpersonateState !== s) {
        this.lastImpersonateState = s;
        // Utente non riletto: lo stato torna sconosciuto, cosi' il giro successivo ci riprova
        // invece di tenere la sessione dell'utente di prima.
        const retryOnNextTick = () => { this.lastImpersonateState = null; };
        if (s) {
          this._appService.getUser().subscribe({
            next: currentUser => this.setUserParams(currentUser),
            error: retryOnNextTick,
          });
        } else {
          lastValueFrom(this._appService.getRealUser())
            .then(realUser => this.setUserParams(realUser))
            .catch(retryOnNextTick);
        }
      }
    });
  }

  ngOnDestroy() {
    this.finalizeOAuthPopup(false, true);
    this.destroy$.next();
    this.destroy$.complete();
    if (this.oauthAutoRefreshSub) {
      this.oauthAutoRefreshSub.unsubscribe();
      this.oauthAutoRefreshSub = null;
    }
    if (this.impersonateAutoRefreshSub) {
      this.impersonateAutoRefreshSub.unsubscribe();
      this.impersonateAutoRefreshSub = null;
    }
  }

  /**
   * Retrieves the bearer token from cookies.
   * @returns The authentication token or null if not found.
   */
  private getBearerToken(): string | null {
    return this.appStateHolder.getCookie('authToken');
  }

}
